import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { get, action } from '@ember/object';
import { alias } from '@ember/object/computed';

import moment from 'moment-timezone';
import SetupGuide from 'cio/models/setup-guide';
import PushService from 'cio/services/push';
import AnalyticsService from 'cio/services/analytics';
import { Platform } from 'cio/utils/push/platforms';
import { MessagingChannelPushCompletionReason } from 'cio/utils/setup-guide/messaging-channels';
import SetupGuideModel from 'cio/models/setup-guide';

export type PlatformDetail = {
  id: Platform;
  name: string;
  header: string;
  message: string;
  completedAt: number;
  skippedAt: number;
  completionReason?: MessagingChannelPushCompletionReason;
};

const PLATFORM_DETAILS: Record<Platform, Partial<PlatformDetail>> = {
  ios: {
    id: 'ios',
    name: 'iOS',
    header: '1. Enable Push for iOS',
    message:
      "Send push messages to customers with iOS devices using Apple's Push Notification Service or Firebase's Cloud Messaging. ",
  },
  android: {
    id: 'android',
    name: 'Android',
    header: '2. Enable Push for Android',
    message: "Send push messages to customers with Android devices using Firebase's Cloud Messaging.",
  },
};

const PLATFORMS: Platform[] = ['ios', 'android'];

export default class SetupGuideChannelsPushController extends Controller {
  @service push!: PushService;
  @service analytics!: AnalyticsService;

  declare model: SetupGuideModel;

  // "model" aliased to add typings without breaking computed properties observing it: Ember 3.13 regression
  // Bug: https://github.com/typed-ember/ember-cli-typescript/issues/1012#issuecomment-565821260
  // long-term fix is using the "declare" syntax introduced in TypeScript 3.7: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-7.html#the-usedefineforclassfields-flag-and-the-declare-property-modifier
  // which is blocked until breaking ember-cli-typescript 4.0.0 release: https://github.com/typed-ember/ember-cli-typescript/issues/1033
  @alias('model') setupGuide!: SetupGuide;

  queryParams = ['configuring'];
  configuring: Platform | '' = '';

  setDefaults() {
    // Sync all the platforms so that the setup guide is updated. It's possible
    // that the user integrates their push platform through the settings
    // section. In that case, when the user comes back to the setup guide, it
    // will be out of sync.
    //
    // To prevent that, we sync all platforms when moving into this route.
    PLATFORMS.forEach((key: Platform) => this.syncPlatform(key));
  }

  get platforms() {
    return PLATFORMS.map((key: Platform) => {
      let { completedAt, completionReason, skippedAt } = this.setupGuide.push[key];

      return {
        ...PLATFORM_DETAILS[key],
        skippedAt,
        completedAt,
        completionReason,
      };
    });
  }

  get allProvidersSetUp() {
    let { push } = this.setupGuide;
    return PLATFORMS.every((platform) => push[platform].completedAt ?? push[platform].skippedAt);
  }

  get allProvidersSkipped() {
    let { push } = this.setupGuide;
    return PLATFORMS.every((platform) => push[platform].skippedAt);
  }

  @action
  completePushChannel() {
    this.setupGuide.updateChannel({
      ...this.setupGuide.push,
      completedAt: moment().unix(),
    });
    return this.setupGuide.save();
  }

  @action
  complete(
    platform: Platform,
    completionReason: MessagingChannelPushCompletionReason = MessagingChannelPushCompletionReason.UserAction,
  ) {
    let pushCopy = { ...this.setupGuide.push };

    if (pushCopy[platform]?.completedAt != null) {
      return;
    }

    pushCopy[platform] = {
      ...pushCopy[platform],
      completedAt: moment().unix(),
      completionReason,
    };

    this.setupGuide.updateChannel(pushCopy);

    return this.setupGuide.save();
  }

  @action
  skip(platform: Platform) {
    let pushCopy = { ...this.setupGuide.push };
    pushCopy[platform] = {
      ...pushCopy[platform],
      skippedAt: moment().unix(),
    };
    this.setupGuide.updateChannel(pushCopy);

    this.analytics.track('onboarding', 'skip_clicked', { step: platform });

    return this.model.save();
  }

  @action
  toggleConfiguring(platform: Platform) {
    if (this.configuring === platform) {
      this.set('configuring', '');
    } else {
      this.set('configuring', platform);
    }
  }

  private syncPlatform(key: Platform) {
    let enabled = this.push.platforms.findBy('key', key);
    let platform = get(this.setupGuide.push, key);

    if (enabled && platform != null && !platform.completedAt) {
      this.complete(key, MessagingChannelPushCompletionReason.ConfigFound);
    }

    return platform;
  }
}
